import { useTranslation } from 'react-i18next';
import { CONDITIONS, conditionKey } from '../../../data/categories';
import { getTokenConfigFor, isRewardCategory } from '../../../utils/tokenConfig';
import {
  AD_TYPE_OPTIONS,
  LISTED_BY_OPTIONS,
  PRICE_TYPE_OPTIONS,
} from './AdFormConstants';
import { FormSection } from './FormSection';

const SECTION_ICON = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

export default function PricingSection({
  values,
  onChange,
  errors,
  handleChange,
  categoryHasListedBy,
}) {
  const { t } = useTranslation();
  // "Bonuses" items are priced in the reward token, everything else in CXBT
  const priceSymbol = getTokenConfigFor(
    isRewardCategory(values.category) ? 'reward' : 'standard'
  ).name;
  return (
    <FormSection
      icon={SECTION_ICON}
      title={t('createAd.pricing')}
      subtitle={t('createAd.setCompetitivePrice')}
    >
      <div className="cf-row">
        <div className="cf-field cf-field--half">
          <label htmlFor="ad-adType">{t('createAd.adType')}</label>
          <select
            id="ad-adType"
            className="cf-input"
            value={values.adType || 'for_sale'}
            onChange={handleChange('adType')}
          >
            {AD_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(`adTypes.${opt.key}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="cf-field cf-field--half">
          <label htmlFor="ad-priceType">{t('createAd.priceType')}</label>
          <select
            id="ad-priceType"
            className="cf-input"
            value={values.priceType || 'fixed'}
            onChange={(e) => {
              const v = e.target.value;
              onChange('priceType', v);
              onChange('negotiable', v === 'negotiable');
            }}
          >
            {PRICE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(`priceTypes.${opt.key}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="cf-field">
        <label htmlFor="ad-price">
          {t('createAd.price')} <span className="cf-req">*</span>
        </label>
        <div className="cf-price-input-wrap">
          <span className="cf-price-symbol">{priceSymbol}</span>
          <input
            id="ad-price"
            className={`cf-input cf-input--price${errors.price ? ' cf-input--error' : ''}`}
            type="text"
            inputMode="decimal"
            name="price"
            placeholder="0"
            value={values.price}
            onChange={(e) => {
              // Allow only valid decimal input. Normalize comma to dot so
              // European users can type "300,50" → stored as "300.50".
              const raw = e.target.value.replace(/[^0-9.,]/g, '');
              const normalized = raw.replace(/,/g, '.');
              // Prevent multiple dots
              const parts = normalized.split('.');
              const cleaned =
                parts.length > 2
                  ? parts[0] + '.' + parts.slice(1).join('')
                  : normalized;
              onChange('price', cleaned);
            }}
          />
        </div>
        {errors.price && <span className="cf-error">{errors.price}</span>}
        <div className="cf-checkbox" style={{ marginTop: 10 }}>
          <input
            id="ad-negotiable"
            type="checkbox"
            checked={values.negotiable}
            onChange={handleChange('negotiable')}
          />
          <label htmlFor="ad-negotiable">{t('createAd.priceNegotiable')}</label>
        </div>
      </div>
      <div className="cf-row">
        <div className="cf-field cf-field--half">
          <label htmlFor="ad-condition">
            {t('createAd.condition')} <span className="cf-req">*</span>
          </label>
          <select
            id="ad-condition"
            className={`cf-input${errors.condition ? ' cf-input--error' : ''}`}
            value={values.condition}
            onChange={handleChange('condition')}
          >
            <option value="">{t('createAd.selectCondition')}</option>
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {t(`conditions.${conditionKey(c)}`)}
              </option>
            ))}
          </select>
          {errors.condition && (
            <span className="cf-error">{errors.condition}</span>
          )}
        </div>
        {!categoryHasListedBy && (
          <div className="cf-field cf-field--half">
            <label htmlFor="ad-listedBy">{t('createAd.listedBy')}</label>
            <select
              id="ad-listedBy"
              className="cf-input"
              value={values.listedBy || ''}
              onChange={handleChange('listedBy')}
            >
              <option value="">{t('createAd.select')}</option>
              {LISTED_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(`listedBy.${opt.key}`)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </FormSection>
  );
}
